
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

interface IDynamicDimensions {
    [context: string]: Record<string, number>;
}

// Define the initial state using the type
const initialState: { allHeights: IDynamicDimensions; allWidths: IDynamicDimensions } = {
  allHeights: {},
  allWidths: {},
};

const dimensionsSlice = createSlice({
  name: 'dimensions',
  initialState,
  reducers: {
    updateDynamicHeight: (state, action: PayloadAction<{context: string, name: string, value: number}>) => {
      const { context, name, value } = action.payload;
      if (!state.allHeights[context]) {
        state.allHeights[context] = {};
      }
      state.allHeights[context][name] = value;
    },
    updateDynamicWidth: (state, action: PayloadAction<{context: string, name: string, value: number}>) => {
      const { context, name, value } = action.payload;
      if (!state.allWidths[context]) {
        state.allWidths[context] = {};
      }
      state.allWidths[context][name] = value;
    },
    // Add other reducers here as needed
  }
});

// Export actions
export const { updateDynamicHeight, updateDynamicWidth } = dimensionsSlice.actions;

// Export reducer
export default dimensionsSlice.reducer;

const selectAllWidths = (state: RootState) => state.dimensions.allWidths;
export const makeGetTotalWidthForContexts = () => createSelector(
  [selectAllWidths, (state: RootState, contexts: string[]) => contexts, (state: RootState, contexts: string[], names?: string[]) => names],
  (allWidths: IDynamicDimensions, contexts: string[], names?: string[]) => contexts.reduce((sumContext: number, context: string) => {
    const contextWidth = Object.values(allWidths[context] || {})
      .reduce((sumItem: number, itemWidth: number, index: number) => {
        // If names are provided, filter by name. Otherwise, sum all item widths.
        if (names) {
          const itemName = Object.keys(allWidths[context] || {})[index];
          if (names.includes(itemName)) {
            return sumItem + itemWidth;
          } else {
            return sumItem;
          }
        }
        return sumItem + itemWidth;
      }, 0);
    return sumContext + contextWidth;
  }, 0)
);
const selectAllHeights = (state: RootState) => state.dimensions.allHeights;
export const makeGetTotalHeightForContexts = () => createSelector(
  [selectAllHeights, (state: RootState, contexts: string[]) => contexts, (state: RootState, contexts: string[], names?: string[]) => names],
  (allHeights: IDynamicDimensions, contexts: string[], names?: string[]) => contexts.reduce((sumContext: number, context: string) => {
    const contextHeight = Object.values(allHeights[context] || {})
      .reduce((sumItem: number, itemHeight: number, index: number) => {
        // If names are provided, filter by name. Otherwise, sum all item heights.
        if (names) {
          const itemName = Object.keys(allHeights[context] || {})[index];
          if (names.includes(itemName)) {
            return sumItem + itemHeight;
          } else {
            return sumItem;
          }
        }
        return sumItem + itemHeight;
      }, 0);
    return sumContext + contextHeight;
  }, 0)
);